import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Folder, FolderOpen, FolderPlus, ChevronRight, ChevronDown, MoreHorizontal, Pencil, FolderInput, Trash2 } from 'lucide-react'
import type { Folder as FolderType } from '../../types'

export interface FolderAction {
  type: 'create' | 'rename' | 'move' | 'delete'
  folder?: FolderType
  parentFolderId?: string | null
}

interface FolderTreeProps {
  folders: FolderType[]
  projectId: string
  currentFolderId: string | null
  isShared?: boolean
  onAction?: (action: FolderAction) => void
}

interface TreeNodeProps {
  folder: FolderType
  children: FolderType[]
  allFolders: FolderType[]
  projectId: string
  currentFolderId: string | null
  isShared?: boolean
  depth: number
  onAction?: (action: FolderAction) => void
}

function TreeNode({ folder, children, allFolders, projectId, currentFolderId, isShared, depth, onAction }: TreeNodeProps) {
  const navigate = useNavigate()
  const [expanded, setExpanded] = useState(depth < 2)
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const isActive = folder.folder_id === currentFolderId
  const hasChildren = children.length > 0
  const prefix = isShared ? '/shared' : ''

  useEffect(() => {
    if (!menuOpen) return
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [menuOpen])

  return (
    <div className="group/folder">
      <div className="flex items-center">
        <button
          onClick={() => navigate(`${prefix}/project/${projectId}/folder/${folder.folder_id}`)}
          className={`flex-1 flex items-center gap-1.5 px-2 py-1.5 text-sm rounded-md transition-colors ${
            isActive ? 'bg-white/50 text-iflow-700 font-medium' : 'text-gray-600 hover:bg-white/30'
          }`}
          style={{ paddingLeft: `${depth * 12 + 8}px` }}
        >
          {hasChildren ? (
            <span
              onClick={(e) => { e.stopPropagation(); e.preventDefault(); setExpanded(!expanded) }}
              className="p-0.5 cursor-pointer"
            >
              {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            </span>
          ) : (
            <span className="w-4" />
          )}
          {expanded && hasChildren ? <FolderOpen size={14} /> : <Folder size={14} />}
          <span className="truncate">{folder.name}</span>
        </button>

        {onAction && !isShared && (
          <div className="relative" ref={menuRef}>
            <button
              onClick={(e) => { e.stopPropagation(); setMenuOpen(!menuOpen) }}
              className="p-1 text-gray-500 hover:text-gray-600 opacity-0 group-hover/folder:opacity-100 transition-opacity"
            >
              <MoreHorizontal size={14} />
            </button>
            {menuOpen && (
              <div className="absolute right-0 top-full mt-1 w-40 light-card shadow-lg py-1 z-20">
                <button
                  onClick={() => { setMenuOpen(false); onAction({ type: 'create', parentFolderId: folder.folder_id }) }}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-white/30"
                >
                  <FolderPlus size={14} /> New Subfolder
                </button>
                <button
                  onClick={() => { setMenuOpen(false); onAction({ type: 'rename', folder }) }}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-white/30"
                >
                  <Pencil size={14} /> Rename
                </button>
                <button
                  onClick={() => { setMenuOpen(false); onAction({ type: 'move', folder }) }}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-white/30"
                >
                  <FolderInput size={14} /> Move to...
                </button>
                <div className="border-t border-sky-200/50 my-1" />
                <button
                  onClick={() => { setMenuOpen(false); onAction({ type: 'delete', folder }) }}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50"
                >
                  <Trash2 size={14} /> Delete
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      {expanded && hasChildren && (
        <div>
          {children.map((child) => (
            <TreeNode
              key={child.folder_id}
              folder={child}
              children={allFolders.filter((f) => f.parent_folder_id === child.folder_id)}
              allFolders={allFolders}
              projectId={projectId}
              currentFolderId={currentFolderId}
              isShared={isShared}
              depth={depth + 1}
              onAction={onAction}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default function FolderTree({ folders, projectId, currentFolderId, isShared, onAction }: FolderTreeProps) {
  const rootFolders = folders.filter((f) => !f.parent_folder_id || f.parent_folder_id === 'root')

  if (!rootFolders.length && !onAction) {
    return <p className="text-xs text-gray-500 px-2">No folders</p>
  }

  return (
    <div className="space-y-0.5">
      {rootFolders.map((folder) => (
        <TreeNode
          key={folder.folder_id}
          folder={folder}
          children={folders.filter((f) => f.parent_folder_id === folder.folder_id)}
          allFolders={folders}
          projectId={projectId}
          currentFolderId={currentFolderId}
          isShared={isShared}
          depth={0}
          onAction={onAction}
        />
      ))}
      {!rootFolders.length && onAction && (
        <p className="text-xs text-gray-500 px-2">No folders yet</p>
      )}
    </div>
  )
}
